import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { motion } from "framer-motion"
import { toast } from "sonner"
import { ArrowLeft, Moon, BarChart3, Smile, Zap, AlertCircle, Check } from "lucide-react"
import { supabase, saveDailyWellness, getTodayWellness, getDailyWellnessRange, createNotification } from "@/lib/supabase"
import { getHooperZone } from "@/lib/monitoring/calculations"
import { useAnalytics } from "@/hooks/useAnalytics"

function computeStreak(entries: { date: string }[]): number {
  const dates = new Set(entries.map(e => e.date))
  let streak = 0
  const today = new Date()
  for (let d = 0; ; d++) {
    const day = new Date(today)
    day.setDate(today.getDate() - d)
    if (dates.has(day.toISOString().split("T")[0])) streak++
    else break
  }
  return streak
}

interface HooperFormProps {
  email: string
}

const SLIDERS = [
  {
    key: "sleep_quality",
    label: "Sommeil",
    icon: Moon,
    iconBg: "rgba(99,102,241,0.15)",
    iconColor: "#818cf8",
    labels: ["Excellent", "Bon", "Moyen", "Mauvais", "Très mauvais"],
  },
  {
    key: "muscle_freshness",
    label: "Fraîcheur musculaire",
    icon: BarChart3,
    iconBg: "rgba(34,197,94,0.15)",
    iconColor: "#4ade80",
    labels: ["Excellent", "Bon", "Moyen", "Mauvais", "Très mauvais"],
  },
  {
    key: "mood",
    label: "Humeur",
    icon: Smile,
    iconBg: "rgba(245,158,11,0.15)",
    iconColor: "#fbbf24",
    labels: ["Excellent", "Bon", "Moyen", "Mauvais", "Très mauvais"],
  },
  {
    key: "soreness",
    label: "Courbatures",
    icon: Zap,
    iconBg: "rgba(168,85,247,0.15)",
    iconColor: "#c084fc",
    labels: ["Excellent", "Bon", "Moyen", "Mauvais", "Très mauvais"],
  },
] as const

export const HooperForm = ({ email: _email }: HooperFormProps) => {
  const navigate = useNavigate()
  const { track } = useAnalytics()
  const [values, setValues] = useState({
    sleep_quality: 3,
    muscle_freshness: 3,
    mood: 3,
    soreness: 3,
  })
  const [painPresent, setPainPresent] = useState(false)
  const [painLocation, setPainLocation] = useState("")
  const [painIntensity, setPainIntensity] = useState(5)
  const [submitting, setSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [alreadySubmitted, setAlreadySubmitted] = useState(false)

  useEffect(() => {
    let mounted = true
    getTodayWellness()
      .then((data) => {
        if (!mounted || !data) return
        setValues({
          sleep_quality: data.sleep_quality,
          muscle_freshness: data.muscle_freshness,
          mood: data.mood,
          soreness: data.soreness,
        })
        setPainPresent(data.pain_present)
        setPainLocation(data.pain_location ?? "")
        setPainIntensity(data.pain_intensity ?? 5)
        setAlreadySubmitted(true)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })
    return () => { mounted = false }
  }, [])

  const score = values.sleep_quality + values.muscle_freshness + values.mood + values.soreness
  const zone = getHooperZone(score)

  const handleSubmit = async () => {
    if (submitting) return
    setSubmitting(true)
    try {
      const today = new Date().toISOString().split("T")[0]
      await saveDailyWellness({
        date: today,
        sleep_quality: values.sleep_quality,
        muscle_freshness: values.muscle_freshness,
        mood: values.mood,
        soreness: values.soreness,
        pain_present: painPresent,
        pain_location: painPresent ? painLocation || null : null,
        pain_intensity: painPresent ? painIntensity : null,
      })
      toast.success("Hooper enregistré !", {
        description: `Score : ${score}/28 — ${zone.label}`,
      })
      track('hooper_filled', { score, status: zone.label })

      // Le rappel « Bilan du matin » du jour n'a plus lieu d'être : il passe en « lu ».
      // Depuis le 04/10 il est écrit dans la cloche même sans push — un client qui
      // remplit son questionnaire sans avoir touché la notification le verrait sinon
      // rester « non lu ».
      void (async () => {
        const { data: { user } } = await supabase.auth.getUser()
        if (!user) return
        await supabase
          .from("notifications").update({ read: true })
          .eq("user_id", user.id).eq("type", "hooper").eq("read", false)
          .gte("created_at", today + "T00:00:00.000Z")
      })()

      // Fire streak check in background — don't block navigation
      void (async () => {
        try {
          const [{ data: { user } }, entries] = await Promise.all([
            supabase.auth.getUser(),
            getDailyWellnessRange(30),
          ])
          if (!user) return
          const streak = computeStreak(entries)
          if (streak > 0 && streak % 5 === 0) {
            track('streak_achieved', { streak_count: streak })
            // La cloche d'abord : son identifiant voyage avec le push, et un clic sur
            // la notification marque exactement cette entrée comme lue.
            const notif = await createNotification({
              type: "achievement",
              title: `${streak} jours de suite !`,
              body: `Tu as rempli ton bilan Hooper ${streak} jours d'affilée.`,
              action_url: "/dashboard/monitoring",
            }).catch((err) => {
              console.error("[HooperForm] cloche de série non écrite :", err)
              return null
            })
            await supabase.functions.invoke("send-push", {
              body: {
                user_id: user.id,
                title: `🔥 ${streak} jours de suite !`,
                body: `Tu enchaînes les bilans matinaux depuis ${streak} jours. Continue comme ça !`,
                url: "/dashboard/monitoring",
                push_type: "streak_achievements",
                notification_id: notif?.id ?? null,
              },
            })
          }
        } catch (err) {
          console.error("[HooperForm] streak push error:", err)
        }
      })()

      navigate("/dashboard/monitoring")
    } catch (err) {
      console.error("Erreur:", err)
      toast.error("Erreur", {
        description: err instanceof Error ? err.message : "Sauvegarde impossible",
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#060a13] flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-cyan-500/20 border-t-cyan-400 animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-[#060a13] cursor-default pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="px-5 pt-5 pb-3"
      >
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => navigate("/dashboard/monitoring")}
            className="flex items-center gap-2 text-gray-400 cursor-pointer hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-[14px]">Retour</span>
          </button>
          <span className="text-[11px] uppercase tracking-wider font-semibold text-cyan-400">
            HOOPER INDEX
          </span>
        </div>
        <h1 className="text-[24px] font-bold text-white mb-1">
          Comment vous sentez-vous ?
        </h1>
        <p className="text-[13px] text-gray-500">
          Questionnaire de votre récupération matinale
        </p>
      </motion.div>

      {/* Already submitted banner */}
      {alreadySubmitted && (
        <motion.div
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mx-5 mt-1 mb-2 flex items-center gap-2.5 px-4 py-3 rounded-xl"
          style={{
            background: "rgba(6,182,212,0.08)",
            border: "1px solid rgba(6,182,212,0.25)",
          }}
        >
          <Check className="w-4 h-4 text-cyan-400 shrink-0" />
          <span className="text-[13px] font-semibold text-cyan-300">
            Déjà enregistré aujourd'hui
          </span>
        </motion.div>
      )}

      {/* Sliders */}
      <div className="px-5 mt-2 space-y-6">
        {SLIDERS.map((slider, idx) => {
          const Icon = slider.icon
          const value = values[slider.key as keyof typeof values]
          return (
            <motion.div
              key={slider.key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + idx * 0.05 }}
            >
              {/* Header avec icône + titre */}
              <div className="flex items-center gap-2.5 mb-3">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ background: slider.iconBg }}
                >
                  <Icon className="w-4 h-4" style={{ color: slider.iconColor }} />
                </div>
                <span className="text-[14px] font-bold text-white">{slider.label}</span>
              </div>

              {/* Labels (5 niveaux) */}
              <div className="flex justify-between text-[10px] text-gray-500 px-1 mb-1.5">
                {slider.labels.map((lbl, i) => (
                  <span key={i}>{lbl}</span>
                ))}
              </div>

              {/* Slider avec gradient vert→rouge */}
              <div className="relative">
                <div
                  className="absolute inset-0 h-1.5 rounded-full top-1/2 -translate-y-1/2"
                  style={{
                    background:
                      "linear-gradient(to right, #22c55e 0%, #84cc16 25%, #eab308 50%, #f97316 75%, #ef4444 100%)",
                  }}
                />
                <input
                  type="range"
                  min="1"
                  max="7"
                  step="1"
                  value={value}
                  onChange={(e) =>
                    !alreadySubmitted && setValues({ ...values, [slider.key]: parseInt(e.target.value) })
                  }
                  className={`relative w-full h-1.5 bg-transparent appearance-none hooper-slider ${alreadySubmitted ? "cursor-default opacity-70 pointer-events-none" : "cursor-pointer"}`}
                  style={{ zIndex: 2 }}
                />
              </div>

              {/* Numéros 1-7 */}
              <div className="flex justify-between text-[10px] text-gray-600 px-1 mt-2">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <span
                    key={n}
                    className={
                      n === value ? "text-cyan-400 font-bold" : ""
                    }
                  >
                    {n}
                  </span>
                ))}
              </div>
            </motion.div>
          )
        })}
      </div>

      {/* Score Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.4 }}
        className="px-5 mt-7"
      >
        <div
          className="rounded-2xl p-5 text-center"
          style={{
            background: "rgba(12,20,35,0.70)",
            border: "1px solid rgba(30,41,59,0.55)",
          }}
        >
          <div
            className="text-[42px] font-extrabold leading-none mb-1"
            style={{ color: zone.color }}
          >
            {score}
          </div>
          <div
            className="text-[14px] font-bold mb-0.5"
            style={{ color: zone.color }}
          >
            {zone.label}
          </div>
          <div className="text-[11px] text-gray-500">Hooper Score</div>
        </div>
      </motion.div>

      {/* Pain section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.45 }}
        className="px-5 mt-5"
      >
        <p className="text-[11px] font-semibold text-cyan-400 uppercase tracking-wider mb-2.5">
          SUIVI DOULEUR
        </p>
        <button
          onClick={() => !alreadySubmitted && setPainPresent(!painPresent)}
          disabled={alreadySubmitted}
          className={`w-full flex items-center gap-3 p-3.5 rounded-xl transition-all ${alreadySubmitted ? "cursor-default opacity-70" : "cursor-pointer hover:bg-white/[0.02]"}`}
          style={{
            background: painPresent ? "rgba(239,68,68,0.05)" : "rgba(12,20,35,0.40)",
            border: painPresent
              ? "1px solid rgba(239,68,68,0.25)"
              : "1px solid rgba(30,41,59,0.55)",
          }}
        >
          <div
            className={`w-5 h-5 rounded-md flex items-center justify-center transition-all ${
              painPresent ? "bg-red-500" : "bg-gray-800"
            }`}
          >
            {painPresent && <Check className="w-3 h-3 text-white" />}
          </div>
          <AlertCircle className="w-4 h-4 text-red-400" />
          <span className="text-[13px] font-semibold text-white flex-1 text-left">
            J'ai une douleur particulière
          </span>
        </button>

        {painPresent && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mt-2 rounded-xl p-4 overflow-hidden"
            style={{
              background: "rgba(239,68,68,0.05)",
              border: "1px solid rgba(239,68,68,0.20)",
            }}
          >
            <div className="mb-3">
              <p className="text-[11px] text-gray-400 mb-1.5">Localisation</p>
              <input
                type="text"
                value={painLocation}
                onChange={(e) => setPainLocation(e.target.value)}
                placeholder="Ex: épaule droite, bas du dos…"
                className="w-full px-3 h-[40px] rounded-lg text-[13px] text-white outline-none"
                style={{
                  background: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(30,41,59,0.55)",
                  caretColor: "#ef4444",
                }}
              />
            </div>
            <div>
              <div className="flex justify-between mb-1.5">
                <p className="text-[11px] text-gray-400">Intensité</p>
                <span className="text-[12px] font-bold text-red-400">{painIntensity}/10</span>
              </div>
              <input
                type="range"
                min="1"
                max="10"
                value={painIntensity}
                onChange={(e) => setPainIntensity(parseInt(e.target.value))}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${
                    ((painIntensity - 1) / 9) * 100
                  }%, #1e293b ${((painIntensity - 1) / 9) * 100}%, #1e293b 100%)`,
                }}
              />
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Submit */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.55 }}
        className="px-5 mt-6"
      >
        <button
          onClick={handleSubmit}
          disabled={submitting || alreadySubmitted}
          className="w-full py-4 rounded-xl text-[14px] font-bold tracking-wide transition-all active:scale-[0.97] disabled:opacity-50 flex items-center justify-center gap-2"
          style={{
            background: alreadySubmitted ? "rgba(6,182,212,0.08)" : "rgba(12,20,35,0.70)",
            border: alreadySubmitted ? "1px solid rgba(6,182,212,0.25)" : "1px solid rgba(30,41,59,0.55)",
            color: alreadySubmitted ? "#67e8f9" : "white",
            cursor: alreadySubmitted ? "default" : "pointer",
          }}
        >
          {submitting && (
            <div className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
          )}
          {submitting ? "ENREGISTREMENT…" : alreadySubmitted ? "DÉJÀ ENREGISTRÉ" : "ENREGISTRER"}
        </button>
      </motion.div>

      {/* Custom slider thumb style */}
      <style>{`
        .hooper-slider::-webkit-slider-thumb {
          appearance: none;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          border: 2px solid #06b6d4;
        }
        .hooper-slider::-moz-range-thumb {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 6px rgba(0,0,0,0.4);
          border: 2px solid #06b6d4;
        }
      `}</style>
    </div>
  )
}
